import { fabric } from "fabric";
import { cssGradient, svgDashProps, svgGradientDef } from "./layerStyle.jsx";

// Unit geometry (100x100 box) for vector shapes — mirrors SHAPE_POINTS in the
// backend render engine so editor, thumbnails and final renders agree.
export const SHAPE_POINTS = {
  triangle: "50,0 100,100 0,100",
  star: "50,0 61,35 98,35 68,57 79,91 50,70 21,91 32,57 2,35 39,35",
  arrow: "0,35 60,35 60,15 100,50 60,85 60,65 0,65",
  hexagon: "25,0 75,0 100,50 75,100 25,100 0,50",
  diamond: "50,0 100,50 50,100 0,50",
  trapezium: "20,0 80,0 100,100 0,100",
  parallelogram: "25,0 100,0 75,100 0,100",
  seal:
    "50.0,0.0 58.4,7.8 69.1,3.8 73.9,14.2 85.4,14.6 85.8,26.1 96.2,30.9 " +
    "92.2,41.6 100.0,50.0 92.2,58.4 96.2,69.1 85.8,73.9 85.4,85.4 " +
    "73.9,85.8 69.1,96.2 58.4,92.2 50.0,100.0 41.6,92.2 30.9,96.2 " +
    "26.1,85.8 14.6,85.4 14.2,73.9 3.8,69.1 7.8,58.4 0.0,50.0 7.8,41.6 " +
    "3.8,30.9 14.2,26.1 14.6,14.6 26.1,14.2 30.9,3.8 41.6,7.8",
};

// Curved shapes as unit-box SVG paths — mirrors SHAPE_PATHS in the backend.
// Each path touches all four edges of the 0-100 box with a flat tangent
// there, so its real bounding box is exactly the unit box (fabric sizes paths
// by bounding box, the server by viewBox; they only agree when these match).
export const SHAPE_PATHS = {
  leaf: "M 0 100 C 0 30 30 0 100 0 C 100 70 70 100 0 100 Z",
  tab: "M 0 0 H 100 V 100 H 40 C 12 100 0 88 0 60 Z",
  heart:
    "M 50 100 C 34 86 0 64 0 30 C 0 12 13 0 28 0 C 40 0 47 8 50 18 " +
    "C 53 8 60 0 72 0 C 87 0 100 12 100 30 C 100 64 66 86 50 100 Z",
  bubble:
    "M 10 0 H 90 C 96 0 100 4 100 10 V 66 C 100 72 96 76 90 76 H 34 L 14 100 " +
    "L 18 76 H 10 C 4 76 0 72 0 66 V 10 C 0 4 4 0 10 0 Z",
  blob1: "M 58 0 C 82 0 100 22 100 46 C 100 78 72 100 40 100 C 14 100 0 80 0 52 C 0 22 30 0 58 0 Z",
  blob2:
    "M 46 0 C 60 0 64 14 76 18 C 90 22 100 34 100 50 C 100 66 92 76 82 82 " +
    "C 70 90 62 100 44 100 C 30 100 26 84 18 78 C 8 70 0 56 0 40 " +
    "C 0 26 4 14 14 12 C 26 10 32 0 46 0 Z",
  blob3:
    "M 30 0 C 50 0 62 10 80 8 C 94 6 100 20 100 36 C 100 52 86 58 88 74 " +
    "C 90 90 74 100 56 100 C 36 100 30 88 16 86 C 4 84 0 72 0 58 " +
    "C 0 42 12 36 10 22 C 8 8 16 0 30 0 Z",
  blob4:
    "M 50 0 C 66 0 74 12 86 18 C 96 24 100 36 100 50 C 100 70 88 76 84 88 " +
    "C 80 98 66 100 52 100 C 36 100 30 90 18 88 C 6 86 0 76 0 62 " +
    "C 0 46 10 40 8 26 C 6 10 30 0 50 0 Z",
};

export const LEAF_DEFAULT_CURVE = 30;

// Leaf with adjustable curviness: c pulls the bezier control points — small
// values give a thin sickle, large values a rounder leaf. Mirrored in the
// backend render engine.
export function leafPath(curve) {
  const c = Math.max(1, Math.min(Number(curve) || LEAF_DEFAULT_CURVE, 60));
  return `M 0 100 C 0 ${c} ${c} 0 100 0 C 100 ${100 - c} ${100 - c} 100 0 100 Z`;
}

export const SHAPE_DEFS = [
  { kind: "rect", label: "Rectangle" },
  { kind: "circle", label: "Circle" },
  { kind: "pill", label: "Pill" },
  { kind: "leaf", label: "Leaf badge" },
  { kind: "tab", label: "Tab card" },
  { kind: "seal", label: "Seal" },
  { kind: "triangle", label: "Triangle" },
  { kind: "star", label: "Star" },
  { kind: "arrow", label: "Arrow" },
  { kind: "hexagon", label: "Hexagon" },
  { kind: "diamond", label: "Diamond" },
  { kind: "trapezium", label: "Trapezium" },
  { kind: "parallelogram", label: "Parallelogram" },
  { kind: "line", label: "Line" },
  { kind: "heart", label: "Heart" },
  { kind: "bubble", label: "Speech bubble" },
];

// Organic shapes, shown in their own row in the Elements panel.
// Elements > Special shapes. Not offered as image frames.
export const SPECIAL_SHAPE_DEFS = [
  { kind: "grid", label: "Grid" },
  { kind: "dot_grid", label: "Dot grid" },
];
export const GRID_KINDS = new Set(["grid", "dot_grid"]);

export const BLOB_DEFS = [
  { kind: "blob1", label: "Blob" },
  { kind: "blob2", label: "Blob" },
  { kind: "blob3", label: "Blob" },
  { kind: "blob4", label: "Blob" },
];

// Shapes an image can be framed in (Elements > Frames, and the image
// Properties "Frame" picker).
export const FRAME_DEFS = [
  { kind: "square", label: "Square" },
  { kind: "circle", label: "Circle" },
  { kind: "pill", label: "Pill" },
  { kind: "hexagon", label: "Hexagon" },
  { kind: "diamond", label: "Diamond" },
  { kind: "trapezium", label: "Trapezium" },
  { kind: "parallelogram", label: "Parallelogram" },
  { kind: "star", label: "Star" },
  { kind: "triangle", label: "Triangle" },
  { kind: "heart", label: "Heart" },
  { kind: "leaf", label: "Leaf" },
  { kind: "tab", label: "Tab" },
  { kind: "seal", label: "Seal" },
  { kind: "blob1", label: "Blob" },
  { kind: "blob2", label: "Blob" },
  { kind: "blob3", label: "Blob" },
  { kind: "blob4", label: "Blob" },
];

export function unitPoints(kind, width, height) {
  return SHAPE_POINTS[kind].split(" ").map((pair) => {
    const [x, y] = pair.split(",").map(Number);
    return { x: (x / 100) * width, y: (y / 100) * height };
  });
}

// Rect outline with independent corner radii [tl, tr, br, bl], in px.
export function roundedRectPath(w, h, radii) {
  const limit = Math.min(w, h) / 2;
  const [tl, tr, br, bl] = radii.map((r) => Math.max(0, Math.min(r || 0, limit)));
  let d = `M ${tl} 0 H ${w - tr}`;
  if (tr) d += ` A ${tr} ${tr} 0 0 1 ${w} ${tr}`;
  d += ` V ${h - br}`;
  if (br) d += ` A ${br} ${br} 0 0 1 ${w - br} ${h}`;
  d += ` H ${bl}`;
  if (bl) d += ` A ${bl} ${bl} 0 0 1 0 ${h - bl}`;
  d += ` V ${tl}`;
  if (tl) d += ` A ${tl} ${tl} 0 0 1 ${tl} 0`;
  return d + " Z";
}

// Polygon outline with every vertex rounded by `radius` px (mirrored in the
// backend render engine so editor and final render agree).
export function roundedPolygonPath(points, radius) {
  if (!radius) {
    return "M " + points.map((p) => `${p.x} ${p.y}`).join(" L ") + " Z";
  }
  const n = points.length;
  let d = "";
  for (let i = 0; i < n; i++) {
    const prev = points[(i - 1 + n) % n];
    const curr = points[i];
    const next = points[(i + 1) % n];
    const v1 = { x: prev.x - curr.x, y: prev.y - curr.y };
    const v2 = { x: next.x - curr.x, y: next.y - curr.y };
    const l1 = Math.hypot(v1.x, v1.y) || 1;
    const l2 = Math.hypot(v2.x, v2.y) || 1;
    const r = Math.min(radius, l1 / 2, l2 / 2);
    const p1 = { x: curr.x + (v1.x / l1) * r, y: curr.y + (v1.y / l1) * r };
    const p2 = { x: curr.x + (v2.x / l2) * r, y: curr.y + (v2.y / l2) * r };
    d += i === 0 ? `M ${p1.x.toFixed(2)} ${p1.y.toFixed(2)}` : ` L ${p1.x.toFixed(2)} ${p1.y.toFixed(2)}`;
    d += ` Q ${curr.x.toFixed(2)} ${curr.y.toFixed(2)} ${p2.x.toFixed(2)} ${p2.y.toFixed(2)}`;
  }
  return d + " Z";
}

// Builds the fabric object for a shape layer (not added to canvas).
// A grid is a Rect that also draws its inner lines, in one path so fill, stroke,
// dash and undo all work as they do for other shapes. The lines keep their
// width when it's resized (strokeUniform). Mirrors _grid_svg in the backend.
const renderRect = fabric.Rect.prototype._render;
fabric.Rect.prototype._render = function (ctx) {
  if (this.shapeKind === "dot_grid") return renderDotGrid.call(this, ctx);
  if (this.shapeKind !== "grid") return renderRect.call(this, ctx);
  const { width: w, height: h } = this;
  const columns = this.gridColumns || 1;
  const rows = this.gridRows || 1;
  ctx.beginPath();
  ctx.rect(-w / 2, -h / 2, w, h);
  for (let i = 1; i < columns; i += 1) {
    ctx.moveTo(-w / 2 + (w * i) / columns, -h / 2);
    ctx.lineTo(-w / 2 + (w * i) / columns, h / 2);
  }
  for (let i = 1; i < rows; i += 1) {
    ctx.moveTo(-w / 2, -h / 2 + (h * i) / rows);
    ctx.lineTo(w / 2, -h / 2 + (h * i) / rows);
  }
  this._renderPaintInOrder(ctx);
};

// A dot grid draws one round dot in the middle of each cell, over its fill.
// Dots stay round and dotSize pixels wide however the box is stretched.
// Mirrors _dot_grid_svg in the backend.
function renderDotGrid(ctx) {
  const { width: w, height: h } = this;
  ctx.beginPath();
  ctx.rect(-w / 2, -h / 2, w, h);
  this._renderFill(ctx);
  const columns = this.gridColumns || 1;
  const rows = this.gridRows || 1;
  const rx = (this.dotSize || 1) / 2 / Math.abs(this.scaleX || 1);
  const ry = (this.dotSize || 1) / 2 / Math.abs(this.scaleY || 1);
  ctx.beginPath();
  for (let c = 0; c < columns; c += 1) {
    for (let r = 0; r < rows; r += 1) {
      const x = -w / 2 + (w * (c + 0.5)) / columns;
      const y = -h / 2 + (h * (r + 0.5)) / rows;
      ctx.moveTo(x + rx, y);
      ctx.ellipse(x, y, rx, ry, 0, 0, Math.PI * 2);
    }
  }
  ctx.fillStyle = this.dotColor || "#191919";
  ctx.fill();
}

export function makeFabricShape(
  kind,
  { width, height, fill, stroke, strokeWidth, left, top, cornerRadius = 0 }
) {
  const common = {
    left,
    top,
    fill: fill || "#cccccc",
    stroke: stroke || "",
    strokeWidth: strokeWidth || 0,
  };

  if (kind === "circle") {
    return new fabric.Ellipse({ ...common, rx: width / 2, ry: height / 2 });
  }
  if (kind === "line" || kind === "square") {
    return new fabric.Rect({ ...common, width, height });
  }
  if (kind === "grid" || kind === "dot_grid") {
    return new fabric.Rect({ ...common, fill, width, height, strokeUniform: true });
  }
  if (kind === "pill") {
    const radius = Math.min(width, height) / 2;
    return new fabric.Rect({ ...common, width, height, rx: radius, ry: radius });
  }
  if (SHAPE_PATHS[kind]) {
    // Path is authored in a 100x100 unit box; scale it to the target size.
    // For leaf, cornerRadius doubles as the curviness parameter.
    const path = kind === "leaf" ? leafPath(cornerRadius || LEAF_DEFAULT_CURVE) : SHAPE_PATHS[kind];
    const obj = new fabric.Path(path, common);
    obj.set({ scaleX: width / 100, scaleY: height / 100 });
    return obj;
  }
  // Polygon shapes (triangle, star, arrow, hexagon, diamond, seal): built in
  // pixel space so vertex rounding is undistorted.
  const points = unitPoints(kind, width, height);
  if (cornerRadius > 0) {
    return new fabric.Path(roundedPolygonPath(points, cornerRadius), common);
  }
  return new fabric.Polygon(points, common);
}

// Small toolbar icon for a shape kind.
export function ShapeIcon({ kind, size = 16 }) {
  const props = { width: size, height: size, viewBox: "0 0 100 100" };
  if (kind === "rect") {
    return (
      <svg {...props}>
        <rect x="5" y="22" width="90" height="56" rx="8" fill="currentColor" />
      </svg>
    );
  }
  if (kind === "dot_grid") {
    return (
      <svg {...props}>
        {[20, 50, 80].flatMap((x) => [20, 50, 80].map((y) => <circle key={`${x}-${y}`} cx={x} cy={y} r="9" fill="currentColor" />))}
      </svg>
    );
  }
  if (kind === "grid") {
    return (
      <svg {...props}>
        <path
          d="M6 6H94V94H6ZM35 6V94M65 6V94M6 35H94M6 65H94"
          fill="none"
          stroke="currentColor"
          strokeWidth="6"
        />
      </svg>
    );
  }
  if (kind === "circle") {
    return (
      <svg {...props}>
        <ellipse cx="50" cy="50" rx="45" ry="45" fill="currentColor" />
      </svg>
    );
  }
  if (kind === "square") {
    return (
      <svg {...props}>
        <rect x="8" y="8" width="84" height="84" rx="4" fill="currentColor" />
      </svg>
    );
  }
  if (kind === "line") {
    return (
      <svg {...props}>
        <rect x="2" y="44" width="96" height="12" rx="6" fill="currentColor" />
      </svg>
    );
  }
  if (kind === "pill") {
    return (
      <svg {...props}>
        <rect x="2" y="28" width="96" height="44" rx="22" fill="currentColor" />
      </svg>
    );
  }
  if (SHAPE_PATHS[kind]) {
    return (
      <svg {...props}>
        <path d={SHAPE_PATHS[kind]} fill="currentColor" />
      </svg>
    );
  }
  return (
    <svg {...props}>
      <polygon points={SHAPE_POINTS[kind] || SHAPE_POINTS.diamond} fill="currentColor" />
    </svg>
  );
}

// SVG element for the read-only template thumbnails.
export function shapeSvg(kind, { fill, stroke, strokeWidth, width = 100, height = 100, radius = 0, gradient = null, dash = "solid", id = "s", columns = 1, rows = 1, dotColor = "#191919", dotSize = 8 }) {
  const gid = `g-${id}`;
  const defs = gradient ? svgGradientDef(gid, gradient) : null;
  const fillValue = gradient ? `url(#${gid})` : fill || "#cccccc";
  const strokeProps = stroke
    ? { stroke, strokeWidth: strokeWidth || 2, vectorEffect: "non-scaling-stroke", ...svgDashProps(dash, strokeWidth || 2) }
    : {};
  if (kind === "circle") {
    return (
      <svg width="100%" height="100%" viewBox="0 0 100 100" preserveAspectRatio="none">
        {defs}
        <ellipse cx="50" cy="50" rx="50" ry="50" fill={fillValue} {...strokeProps} />
      </svg>
    );
  }
  if (kind === "dot_grid") {
    const dots = [];
    for (let c = 0; c < columns; c += 1) {
      for (let r = 0; r < rows; r += 1) dots.push([(width * (c + 0.5)) / columns, (height * (r + 0.5)) / rows]);
    }
    return (
      <svg width="100%" height="100%" viewBox={`0 0 ${width} ${height}`}>
        {defs}
        <rect x="0" y="0" width={width} height={height} fill={fillValue} />
        {dots.map(([x, y]) => <circle key={`${x}-${y}`} cx={x} cy={y} r={dotSize / 2} fill={dotColor} />)}
      </svg>
    );
  }
  if (kind === "grid") {
    const verticals = Array.from({ length: columns - 1 }, (_, i) => (100 * (i + 1)) / columns);
    const horizontals = Array.from({ length: rows - 1 }, (_, i) => (100 * (i + 1)) / rows);
    return (
      <svg width="100%" height="100%" viewBox="0 0 100 100" preserveAspectRatio="none">
        {defs}
        <rect x="0" y="0" width="100" height="100" fill={fillValue} {...strokeProps} />
        {verticals.map((x) => <line key={`v${x}`} x1={x} x2={x} y1="0" y2="100" {...strokeProps} />)}
        {horizontals.map((y) => <line key={`h${y}`} y1={y} y2={y} x1="0" x2="100" {...strokeProps} />)}
      </svg>
    );
  }
  if (kind === "line") {
    return (
      <svg width="100%" height="100%" viewBox="0 0 100 100" preserveAspectRatio="none">
        {defs}
        <rect x="0" y="0" width="100" height="100" fill={fillValue} {...strokeProps} />
      </svg>
    );
  }
  if (kind === "pill") {
    return (
      <div
        style={{
          width: "100%",
          height: "100%",
          background: gradient ? cssGradient(gradient) : fillValue,
          borderRadius: 9999,
          border: stroke ? `${strokeWidth || 2}px ${dash || "solid"} ${stroke}` : "none",
        }}
      />
    );
  }
  if (SHAPE_PATHS[kind]) {
    const d = kind === "leaf" ? leafPath(radius || LEAF_DEFAULT_CURVE) : SHAPE_PATHS[kind];
    return (
      <svg width="100%" height="100%" viewBox="0 0 100 100" preserveAspectRatio="none">
        {defs}
        <path d={d} fill={fillValue} {...strokeProps} />
      </svg>
    );
  }
  // Polygon shapes: pixel-space path so vertex rounding is undistorted.
  const kindKey = SHAPE_POINTS[kind] ? kind : "diamond";
  const d = roundedPolygonPath(unitPoints(kindKey, width, height), radius);
  return (
    <svg width="100%" height="100%" viewBox={`0 0 ${width} ${height}`}>
      {defs}
      <path d={d} fill={fillValue} {...strokeProps} />
    </svg>
  );
}
